import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { User, Lock, Eye, EyeOff, Save, ArrowLeft, Shield, Key } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { postRequest } from "../../routes/Routes";
import { getUser } from "../../auth/Auth";

/* Reusable password field with rock-solid eye positioning */
function PasswordField({
    label,
    name,
    value,
    onChange,
    show,
    onToggle,
    placeholder,
    required = true,
}) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
            <div className="relative">
                <input
                    type={show ? "text" : "password"}
                    name={name}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    required={required}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                />
                <button
                    type="button"
                    onClick={onToggle}
                    aria-label={show ? "Hide password" : "Show password"}
                    aria-pressed={show}
                    className="absolute inset-y-0 right-3 z-10 inline-flex items-center justify-center"
                >
                    {show ? <EyeOff className="w-5 h-5 text-gray-500" /> : <Eye className="w-5 h-5 text-gray-500" />}
                </button>
            </div>
        </div>
    );
}

const UserSettings = () => {
    const [user, setUser] = useState(undefined);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    useEffect(() => {
        getUser()
            .then((data) => setUser(data))
            .catch(() => setUser(null));
    }, []);

    const handleChange = (e) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        if (message.text) setMessage({ type: "", text: "" });
    };

    const togglePasswordVisibility = (field) =>
        setShowPasswords((p) => ({ ...p, [field]: !p[field] }));

    const validateForm = () => {
        if (!formData.currentPassword || !formData.newPassword) {
            setMessage({ type: "error", text: "Current password and new password are required" });
            return false;
        }
        if (formData.newPassword.length < 6) {
            setMessage({ type: "error", text: "New password must be at least 6 characters long" });
            return false;
        }
        if (formData.newPassword !== formData.confirmPassword) {
            setMessage({ type: "error", text: "New passwords do not match" });
            return false;
        }
        if (formData.currentPassword === formData.newPassword) {
            setMessage({ type: "error", text: "New password must be different from current password" });
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        if (!user?.token) {
            setMessage({ type: "error", text: "You are not logged in." });
            return;
        }

        setIsLoading(true);
        try {
            const res = await postRequest({
                endpoint: "/api/user/update-password",
                token: user.token,
                // backend expects snake_case keys
                current_password: formData.currentPassword,
                new_password: formData.newPassword,
            });

            if (res?.data && res.data.error === false) {
                setMessage({ type: "success", text: res.data.message || "Password updated successfully!" });
                setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
            } else {
                setMessage({
                    type: "error",
                    text: res?.data?.message || "Failed to update password.",
                });
            }
        } catch (err) {
            setMessage({ type: "error", text: "Something went wrong. Please try again." });
        } finally {
            setIsLoading(false);
        }
    };

    const getPasswordStrength = (password) => {
        if (!password) return { strength: 0, label: "", color: "" };
        let strength = 0;
        if (password.length >= 6) strength += 1;
        if (password.length >= 8) strength += 1;
        if (/[A-Z]/.test(password)) strength += 1;
        if (/[0-9]/.test(password)) strength += 1;
        if (/[^A-Za-z0-9]/.test(password)) strength += 1;
        const levels = [
            { strength: 0, label: "", color: "" },
            { strength: 1, label: "Very Weak", color: "bg-red-500" },
            { strength: 2, label: "Weak", color: "bg-orange-500" },
            { strength: 3, label: "Fair", color: "bg-yellow-500" },
            { strength: 4, label: "Good", color: "bg-blue-500" },
            { strength: 5, label: "Strong", color: "bg-green-500" },
        ];
        return levels[strength];
    };

    const passwordStrength = getPasswordStrength(formData.newPassword);

    return (
        <div className="p-8">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <button
                        onClick={() => navigate("/portal/qrs")}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">User Settings</h1>
                        <p className="text-gray-600">Manage your account security and preferences</p>
                    </div>
                </div>

                {/* Card */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 text-white">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                                <User className="w-8 h-8" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold">Admin User</h2>
                                <p className="text-purple-100">Portal Administrator</p>
                            </div>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                <Lock className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold text-gray-800">Change Password</h3>
                                <p className="text-gray-600 text-sm">Update your password to keep your account secure</p>
                            </div>
                        </div>

                        {message.text && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`p-4 rounded-xl mb-6 ${message.type === "success"
                                        ? "bg-green-100 text-green-800 border border-green-200"
                                        : "bg-red-100 text-red-800 border border-red-200"
                                    }`}
                            >
                                <div className="flex items-center gap-2">
                                    {message.type === "success" ? <Shield className="w-5 h-5" /> : <Key className="w-5 h-5" />}
                                    {message.text}
                                </div>
                            </motion.div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <PasswordField
                                label="Current Password"
                                name="currentPassword"
                                value={formData.currentPassword}
                                onChange={handleChange}
                                show={showPasswords.current}
                                onToggle={() => togglePasswordVisibility("current")}
                                placeholder="Enter your current password"
                            />

                            <div>
                                <PasswordField
                                    label="New Password"
                                    name="newPassword"
                                    value={formData.newPassword}
                                    onChange={handleChange}
                                    show={showPasswords.new}
                                    onToggle={() => togglePasswordVisibility("new")}
                                    placeholder="Enter your new password"
                                />

                                {formData.newPassword && (
                                    <div className="mt-2">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                <div
                                                    className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                                                    style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                                                />
                                            </div>
                                            <span className="text-sm text-gray-600">{passwordStrength.label}</span>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            Use 8+ characters with uppercase, lowercase, numbers, and symbols
                                        </p>
                                    </div>
                                )}
                            </div>

                            <PasswordField
                                label="Confirm New Password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                show={showPasswords.confirm}
                                onToggle={() => togglePasswordVisibility("confirm")}
                                placeholder="Confirm your new password"
                            />

                            <div className="pt-4">
                                <motion.button
                                    type="submit"
                                    disabled={isLoading}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? (
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Save className="w-5 h-5" />
                                            Update Password
                                        </>
                                    )}
                                </motion.button>
                            </div>
                        </form>

                        <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <Shield className="w-5 h-5 text-purple-600" />
                                Security Tips
                            </h4>
                            <ul className="text-sm text-gray-600 space-y-2">
                                <li>• Use a unique password that you don't use elsewhere</li>
                                <li>• Include a mix of letters, numbers, and special characters</li>
                                <li>• Avoid using personal information in your password</li>
                                <li>• Consider using a password manager</li>
                                <li>• Change your password regularly</li>
                            </ul>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default UserSettings;
